import { RefObject } from "react";

interface RenderParams {
  canvasRef: RefObject<HTMLCanvasElement>;
  resultBlockId: string;
}

interface Draggable {
  element: HTMLElement;
  zIndex: number;
  isMain: boolean;
}

export const renderToCanvas = async ({
  canvasRef,
  resultBlockId,
}: RenderParams): Promise<void> => {
  const resultBlock = getResultBlock(resultBlockId);
  const canvas = canvasRef.current;
  const ctx = canvas?.getContext("2d");

  if (!resultBlock || !canvas || !ctx) {
    console.error("Missing resultBlock, canvas, or context");
    return;
  }

  const { scaleX, scaleY } = computeScales(canvas, resultBlock);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const draggables = getDraggableElements(resultBlock);
  const ordered = sortByZIndex(draggables);

  await Promise.all(
    ordered.map((d) => drawDraggable(ctx, d, resultBlock, scaleX, scaleY)),
  );
};

function getResultBlock(id: string): HTMLElement | null {
  const el = document.getElementById(id);
  return el?.parentElement || null;
}

function computeScales(
  canvas: HTMLCanvasElement,
  block: HTMLElement,
): { scaleX: number; scaleY: number } {
  const rect = block.getBoundingClientRect();
  return {
    scaleX: canvas.width / rect.width,
    scaleY: canvas.height / rect.height,
  };
}

function getDraggableElements(block: HTMLElement): Draggable[] {
  return Array.from(
    block.querySelectorAll<HTMLElement>("[data-draggable='draggable']"),
  ).map((el, idx) => {
    const parentZ = el.closest(".zIndexCopy");
    const z = parentZ
      ? parseInt(getComputedStyle(parentZ).zIndex || "0", 10)
      : 0;
    return {
      element: el,
      zIndex: idx === 0 ? 4 : z || 4,
      isMain: idx === 0,
    };
  });
}

function sortByZIndex(arr: Draggable[]): Draggable[] {
  return arr.slice().sort((a, b) => {
    if (a.zIndex !== b.zIndex) {
      return a.zIndex - b.zIndex;
    }
    if (a.isMain) {
      return 1;
    }
    return -1;
  });
}

function drawDraggable(
  ctx: CanvasRenderingContext2D,
  { element }: Draggable,
  block: HTMLElement,
  scaleX: number,
  scaleY: number,
): Promise<void> {
  const imgEl = element.querySelector<HTMLElement>("[data-draggable='image']");
  if (!imgEl) {
    console.warn("No image container found");
    return Promise.resolve();
  }

  const bg = getComputedStyle(imgEl).backgroundImage;
  if (bg === "none") {
    return Promise.resolve();
  }

  const url = bg.slice(5, -2);
  return loadAndDrawImage(
    ctx,
    url,
    element.getBoundingClientRect(),
    block.getBoundingClientRect(),
    scaleX,
    scaleY,
  );
}

function loadAndDrawImage(
  ctx: CanvasRenderingContext2D,
  src: string,
  elRect: DOMRect,
  blockRect: DOMRect,
  scaleX: number,
  scaleY: number,
): Promise<void> {
  return new Promise((res) => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      const left = (elRect.left - blockRect.left) * scaleX;
      const top = (elRect.top - blockRect.top) * scaleY;
      const width = elRect.width * scaleX;
      const height = elRect.height * scaleY;
      ctx.drawImage(img, left, top, width, height);
      res();
    };
    img.onerror = () => {
      console.error("Failed to load image:", src);
      res();
    };
  });
}

interface InitCanvasEventsParams {
  canvasDom: HTMLElement;
  canvasRef2: RefObject<HTMLCanvasElement>;
  virtualCanvasRef: RefObject<HTMLCanvasElement>;
  onNewRender: () => void;
  changeZoom: (newZoom: number) => number;
  zoomRef: { current: number };
}

interface ResizeParams {
  dragEl: HTMLElement;
  containerEl: HTMLElement;
  start: {
    x: number;
    y: number;
    left: number;
    top: number;
    width: number;
    height: number;
  };
  dir: { x: number; y: number };
  shift: { x: number; y: number };
  zoom: number;
  cleanup: () => void;
}

function createResizeHandlers({
  dragEl,
  containerEl,
  start,
  dir,
  shift,
  zoom,
  cleanup,
}: ResizeParams) {
  const resizeHorizontal = (deltaX: number) => {
    if (dir.x === 1) {
      const newW = Math.min(
        Math.max(0, start.width + deltaX),
        containerEl.offsetWidth - start.left,
      );
      dragEl.style.width = `${newW}px`;
    } else {
      const boundary = containerEl.getBoundingClientRect().left / zoom;
      if ((start.x + deltaX) * dir.x < boundary) {
        const oldLeft = dragEl.offsetLeft - shift.x;
        dragEl.style.width = `${dragEl.offsetWidth + oldLeft}px`;
        dragEl.style.left = `${shift.x}px`;
      } else {
        dragEl.style.width = `${Math.max(0, start.width + deltaX)}px`;
        dragEl.style.left = `${start.left - deltaX}px`;
      }
    }
  };

  const resizeVertical = (deltaY: number) => {
    if (dir.y === 1) {
      const newH = Math.min(
        Math.max(0, start.height + deltaY),
        containerEl.offsetHeight - start.top,
      );
      dragEl.style.height = `${newH}px`;
    } else {
      const boundaryY = containerEl.getBoundingClientRect().top / zoom;
      if ((start.y + deltaY) * dir.y < boundaryY) {
        const oldTop = dragEl.offsetTop - shift.y;
        dragEl.style.height = `${dragEl.offsetHeight + oldTop}px`;
        dragEl.style.top = `${shift.y}px`;
      } else {
        dragEl.style.height = `${Math.max(0, start.height + deltaY)}px`;
        dragEl.style.top = `${start.top - deltaY}px`;
      }
    }
  };

  function onPointerMove(e: PointerEvent) {
    const currX = e.clientX / zoom;
    const currY = e.clientY / zoom;
    const deltaX = (currX - start.x) * dir.x;
    const deltaY = (currY - start.y) * dir.y;

    resizeHorizontal(deltaX);
    resizeVertical(deltaY);
  }

  function onPointerUp(e: PointerEvent) {
    dragEl.releasePointerCapture(e.pointerId);
    cleanup();
  }

  return { onPointerMove, onPointerUp };
}

export function initializeCanvasEvents({
  canvasDom,
  canvasRef2,
  virtualCanvasRef,
  onNewRender,
  changeZoom,
  zoomRef,
}: InitCanvasEventsParams) {
  const zIndexFront = 5;
  const zIndexBack = 3;

  // Zoom via Ctrl+wheel
  const handleWheel = (event: WheelEvent) => {
    if (!event.ctrlKey) return;
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.05 : 0.05;
    zoomRef.current = changeZoom(zoomRef.current + delta);
  };

  // Resize down event
  const resizePointerDown = (event: PointerEvent) => {
    const target = event.target as HTMLElement;
    const containerEl = target.closest<HTMLElement>(
      '[data-draggable="container"]',
    );
    const dragEl = target.closest<HTMLElement>('[data-draggable="draggable"]');
    const dotEl = target.closest<HTMLElement>('[data-draggable="resize"]');

    if (!containerEl || !dragEl || !dotEl) return;

    event.stopImmediatePropagation();
    dragEl.setPointerCapture(event.pointerId);

    const start = {
      x: event.clientX / zoomRef.current,
      y: event.clientY / zoomRef.current,
      left: dragEl.offsetLeft,
      top: dragEl.offsetTop,
      width: dragEl.offsetWidth,
      height: dragEl.offsetHeight,
    };
    const dir = {
      x: Number(dotEl.dataset.x) || 1,
      y: Number(dotEl.dataset.y) || 1,
    };
    const rect = containerEl.getBoundingClientRect();
    const parent = (dragEl.offsetParent as HTMLElement).getBoundingClientRect();
    const shift = { x: rect.left - parent.left, y: rect.top - parent.top };

    const { onPointerMove, onPointerUp } = createResizeHandlers({
      dragEl,
      containerEl,
      start,
      dir,
      shift,
      zoom: zoomRef.current,
      cleanup: () => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      },
    });

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const movePointerDown = (event: PointerEvent) => {
    const target = event.target as HTMLElement;
    const containerEl = target.closest<HTMLElement>(
      '[data-draggable="container"]',
    );
    const dragEl = target.closest<HTMLElement>('[data-draggable="draggable"]');
    if (!containerEl || !dragEl) return;

    dragEl.setPointerCapture(event.pointerId);

    const startX = event.clientX / zoomRef.current;
    const startY = event.clientY / zoomRef.current;
    const startLeft = dragEl.offsetLeft;
    const startTop = dragEl.offsetTop;
    const rect = containerEl.getBoundingClientRect();
    const offsetParent = (
      dragEl.offsetParent as HTMLElement
    ).getBoundingClientRect();
    const shiftX = rect.left - offsetParent.left;
    const shiftY = rect.top - offsetParent.top;

    function onPointerMove(e: PointerEvent) {
      const currX = e.clientX / zoomRef.current;
      const currY = e.clientY / zoomRef.current;
      const dx = currX - startX;
      const dy = currY - startY;

      dragEl.style.left = `${Math.min(
        Math.max(shiftX, startLeft + dx),
        rect.width - dragEl.offsetWidth + shiftX,
      )}px`;
      dragEl.style.top = `${Math.min(
        Math.max(shiftY, startTop + dy),
        rect.height - dragEl.offsetHeight + shiftY,
      )}px`;
    }

    function onPointerUp() {
      dragEl.releasePointerCapture(event.pointerId);
      dragEl.removeEventListener("pointermove", onPointerMove);
      dragEl.removeEventListener("pointerup", onPointerUp);
      renderToCanvas({
        canvasRef: canvasRef2,
        resultBlockId: "imagesItemWorkspace",
      });
      renderToCanvas({
        canvasRef: virtualCanvasRef,
        resultBlockId: "imagesItemWorkspace",
      });
      onNewRender();
    }

    dragEl.addEventListener("pointermove", onPointerMove);
    dragEl.addEventListener("pointerup", onPointerUp);
  };

  const handleClick = (event: MouseEvent) => {
    const btn = (event.target as HTMLElement).closest<HTMLElement>(
      '[data-draggable="button"]',
    );
    if (!btn) return;
    const pos = btn.dataset.position === "back" ? zIndexBack : zIndexFront;
    document
      .querySelectorAll<HTMLElement>(".zIndexCopy")
      .forEach((el) => (el.style.zIndex = `${pos}`));
  };

  canvasDom.addEventListener("pointerdown", resizePointerDown);
  canvasDom.addEventListener("pointerdown", movePointerDown);
  canvasDom.addEventListener("wheel", handleWheel, { passive: false });
  canvasDom.addEventListener("mousedown", handleClick);

  return () => {
    canvasDom.removeEventListener("pointerdown", resizePointerDown);
    canvasDom.removeEventListener("pointerdown", movePointerDown);
    canvasDom.removeEventListener("wheel", handleWheel);
    canvasDom.removeEventListener("mousedown", handleClick);
  };
}
